import { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Sparkles, Loader2, CheckCircle2 } from "lucide-react";

// Prompt-to-Crawl (specs/prompt-to-crawl-spec.md): a single text field, no
// separate config form for this path -- the backend extracts a name/
// keywords/seed URLs from the prompt, verifies every candidate seed URL
// with a real HTTP request, and launches the crawl immediately with no
// review step. This panel just shows what got decided after the fact.
export default function PromptToCrawl({ onLaunched }) {
  const [prompt, setPrompt] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const launch = async () => {
    const trimmed = prompt.trim();
    if (!trimmed) { toast.error("Describe what data you want first"); return; }
    setBusy(true);
    setResult(null);
    try {
      const r = await api.promptToCrawl(trimmed);
      setResult(r.plan);
      toast.success(`"${r.crawl.name}" launched from your prompt`);
      onLaunched?.(r.crawl);
      setPrompt("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not build a crawl from that prompt");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="prompt-to-crawl" className="rounded-md border border-violet-500/30 bg-violet-500/5 p-3.5 space-y-3">
      <div className="flex items-center gap-2">
        <Sparkles className="h-3.5 w-3.5 text-violet-400" />
        <span className="font-mono text-[10px] uppercase tracking-widest text-slate-400">Prompt-to-Crawl</span>
        <span className="ml-auto font-mono text-[10px] text-slate-600">describe it, we build + launch it</span>
      </div>
      <Textarea
        data-testid="prompt-to-crawl-input"
        value={prompt}
        onChange={(e) => setPrompt(e.target.value)}
        placeholder="e.g. crawl diesel diagnostic forums and fault code databases for training data"
        rows={2}
        disabled={busy}
        className="bg-[#0B0F18] border-border font-mono text-sm resize-none"
      />
      <Button
        data-testid="prompt-to-crawl-button"
        onClick={launch}
        disabled={busy}
        className="w-full bg-violet-500 hover:bg-violet-400 text-slate-950 font-semibold"
      >
        {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1.5" />}
        {busy ? "Building & launching..." : "Build & Launch"}
      </Button>
      {result && (
        <div data-testid="prompt-to-crawl-result" className="rounded border border-violet-500/40 bg-violet-500/10 px-3 py-2.5 space-y-1">
          <div className="flex items-center gap-1.5 text-violet-200">
            <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
            <span className="font-mono text-xs truncate">{result.name}</span>
          </div>
          <p className="font-mono text-[10px] text-violet-300/80">
            {result.keywords.length} keywords, {result.seed_urls.length} seed URLs
            {" "}({result.discovery_method}, {result.candidates_verified}/{result.candidates_found} verified)
            {" "}-- quality &gt;= {result.llm_quality_threshold}
          </p>
          <p className="font-mono text-[10px] text-slate-500 break-all">{result.seed_urls.join(", ")}</p>
        </div>
      )}
    </div>
  );
}
